/** ****************** IMPORTS ********************/
import { Grid, makeStyles } from '@material-ui/core';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { SingleDatePicker } from 'react-dates';

import { TimePickerCustom } from '~/src/dash/components/datepicker/timepicker-custom';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { getToday, overrideTimezoneForDate } from '~/src/dash/utils/date';
import { usePublicaTranslation } from '~/src/dash/publicaUI';

/** ****************** INTERFACES ********************/
interface IPresets {
  date: moment.Moment;
  title: string;
}

interface IProps {
  placeholder: string;
  type: 'start' | 'end';
  date: moment.Moment | null;
  minDate?: moment.Moment | null;
  maxDate?: moment.Moment | null;
  disableTime?: boolean;
  timezone?: string;
  handleDateChange: (type: string, date: moment.Moment | undefined) => void;
  enableSelectAll?: boolean;
  enableClearSelection?: boolean;
  enablePresets?: boolean;
  openDirection?: 'up' | 'down';
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
  smallCalendar?: boolean;
}

/** ****************** useStyle ********************/
const useStyles = makeStyles({
  datePickerLabel: {
    height: '20px',
  },
  helperText: {
    color: '#e53935',
    position: 'absolute',
    margin: 0,
    fontSize: '12px',
    left: '8px',
    top: '95%',
  },
});

/** ****************** Helper Functions ********************/
const getMonthDate = (today: moment.Moment, type: string, numOfMonths: number) => {
  if (type === 'end') {
    return today.clone().add(numOfMonths, 'months').endOf('months');
  } else {
    return today.clone().add(numOfMonths, 'months').startOf('months');
  }
};

/** ****************** MAIN COMPONENT ********************/
export const DateTimePickerCustom = (props: IProps) => {
  const classes = useStyles();
  const { t } = usePublicaTranslation();

  // Props
  const {
    date,
    disableTime,
    handleDateChange,
    placeholder,
    timezone,
    type,
    enableSelectAll,
    enableClearSelection,
    enablePresets,
    openDirection,
    minDate,
    maxDate,
    disabled,
    helperText,
    error,
    smallCalendar,
  } = props;

  /** ******** States **********/

  const [hour, setHour] = useState<number | null>(date ? date.hour() : null);
  const [minute, setMinute] = useState<number | null>(date ? date.minute() : null);

  // Date Picker Focus State
  const [focused, setFocused] = useState<boolean | null>(false);

  // Preset Date States
  const [today, setToday] = useState<moment.Moment>(getToday(timezone || 'UTC'));
  const [oneHour, setOneHour] = useState<moment.Moment>(today.clone().add(1, 'hours'));
  const [nextMonth, setNextMonth] = useState<moment.Moment>(getMonthDate(today, type, 1));
  const [twoMonths, setTwoMonths] = useState<moment.Moment>(getMonthDate(today, type, 2));
  const [presets, setPresets] = useState<IPresets[]>([]);

  // State just to keep track of First Render
  const [firstRender, setFirstRender] = useState<boolean | null>(true);

  /** ******** Use Effects **********/

  // Loads Preset Array with Correct Values based on 'type'
  useEffect(() => {
    setPresets(
      type === 'end'
        ? [
            { date: nextMonth, title: nextMonth.format('MMM Do') },
            { date: twoMonths, title: twoMonths.format('MMM Do') },
          ]
        : [
            { date: today, title: t('Immediately') },
            { date: oneHour, title: t('OneHourFromNow') },
            { date: nextMonth, title: nextMonth.format('MMM Do') },
            { date: twoMonths, title: twoMonths.format('MMM Do') },
          ],
    );
  }, [today, oneHour, nextMonth, twoMonths]);

  // If timezone is changed, changes all timezones (skips first render)
  useEffect(() => {
    if (!firstRender) {
      setToday(getToday(timezone || 'UTC'));
      setOneHour(oneHour.clone().tz(timezone || 'UTC'));
      setNextMonth(nextMonth.clone().tz(timezone || 'UTC'));
      setTwoMonths(twoMonths.clone().tz(timezone || 'UTC'));
    }
    setFirstRender(false);
  }, [timezone]);

  useEffect(() => {
    if (date) {
      setHour(date.hour());
      setMinute(date.minute());
    }
  }, [date]);

  // When hour or minute is updated it will update the date with these new times
  // skips if date doesnt exist or if hour and null both don't exist
  useEffect(() => {
    if (!date || (hour === null && minute === null)) {
      return;
    }
    const newDate = date.clone();
    if (hour !== null && hour !== newDate.hour()) {
      newDate.hour(hour);
    }
    if (minute !== null && minute !== newDate.minute()) {
      newDate.minute(minute);
    }
    if (!newDate.isSame(date)) {
      handleDateChange(type, newDate);
    }
  }, [hour, minute]);

  /** ******** HANDLE FUNCTIONS **********/

  const handleTimeChange = (newHour: number | undefined, newMinute: number | undefined) => {
    if (newHour !== undefined && newHour !== hour) {
      setHour(newHour);
    }
    if (newMinute !== undefined && newMinute !== minute) {
      setMinute(newMinute);
    }
  };

  const handleAmPmChange = (amPm: string) => {
    if (hour === null) {
      return;
    }

    if (amPm === 'AM' && hour >= 12) {
      setHour(hour - 12);
    } else if (amPm === 'PM' && hour < 12) {
      setHour(hour + 12);
    }
  };

  const outsideRange = (day: moment.Moment) => {
    if (minDate && maxDate) {
      return day.isBefore(minDate, 'day') || day.isAfter(maxDate, 'day');
    } else if (minDate) {
      return day.isBefore(minDate, 'day');
    } else if (maxDate) {
      return day.isAfter(maxDate, 'day');
    }

    if (enableSelectAll) {
      return false;
    } else {
      return day.isBefore(today, 'day');
    }
  };

  /** ******** PRESET SECTION **********/

  // Sets Date to Selected Preset
  const setPresetDate = (date: moment.Moment) => {
    handleDateChange(type, date);
    setFocused(false);
  };

  // Render
  const renderPresets = () => {
    return (
      <Grid container direction='column' style={{ paddingRight: '20px' }}>
        <label style={{ marginTop: '22px', marginBottom: '5px' }}>
          {`${type === 'start' ? 'Start' : 'End'} Date Presets`}
        </label>
        {presets.map(({ date, title }) => (
          <ModelSecondaryButton
            style={{ justifyContent: 'flex-start' }}
            onClick={() => setPresetDate(date)}
          >
            {title}
          </ModelSecondaryButton>
        ))}
      </Grid>
    );
  };
  /** ******** MAIN RENDER RETURN **********/
  return (
    <Grid
      container
      style={{
        width: 'auto',
        marginRight: type === 'start' ? '30px' : '0px',
        marginBottom: error ? '15px' : '0px',
        position: 'relative',
      }}
      direction='row'
      wrap='nowrap'
      spacing={2}
      className={error ? 'CustomDateTimePicker' : ''}
    >
      <Grid
        item
        container
        direction='column'
        style={{ width: 'min-content', margin: 0 }}
        spacing={1}
      >
        <label className={classes.datePickerLabel} style={{ marginBottom: '0.25em' }}>
          {placeholder}
        </label>
        <SingleDatePicker
          id={type}
          date={date}
          isOutsideRange={outsideRange}
          onDateChange={(newDate) => {
            // If no date, dont update date

            if (!newDate) {
              return;
            }

            newDate = overrideTimezoneForDate(newDate, timezone || 'UTC');

            // Based on type, sets default time shown (startOf = 12 AM, endOf = 11:59 PM)
            if (type === 'start') {
              newDate.startOf('days');
            } else if (type === 'end') {
              newDate.endOf('days');
            }

            // If you can select time, then use the hour and minute state to keep the hour and minute
            // This essentially only lets the date picker update just the date
            // The time picker will let you change the time
            // If time picker is off, it should default to beginning or end of day
            if (!disableTime && hour !== null) {
              newDate.hour(hour);
            }
            if (!disableTime && minute !== null) {
              newDate.minute(minute);
            }

            // Update date
            handleDateChange(type, newDate);
          }}
          focused={focused}
          onFocusChange={({ focused }) => setFocused(focused)}
          numberOfMonths={1}
          placeholder={placeholder ? placeholder : undefined}
          renderCalendarInfo={enablePresets ? renderPresets : undefined}
          calendarInfoPosition='after'
          hideKeyboardShortcutsPanel={true}
          small={true}
          openDirection={openDirection}
          disabled={!!disabled}
          daySize={smallCalendar ? 31 : undefined}
        />
        {error && <div className={classes.helperText}>{helperText}</div>}
      </Grid>
      {!disableTime && (
        <TimePickerCustom
          date={date}
          type={type}
          enableClearSelection={enableClearSelection}
          openDirection={openDirection}
          handleTimeChange={handleTimeChange}
          handleAmPmChange={handleAmPmChange}
          disabled={disabled}
          error={error}
        />
      )}
    </Grid>
  );
};
